import { type ReactNode, type FC } from "react";
import { useFormContext } from "react-hook-form";
import { Checkbox } from "@root/shared/components/Checkbox";
import { TextArea } from "@root/shared/components/TextArea";
import { InformationText } from "@root/shared/components/InformationText";
import { Heading } from "@root/shared/components/Heading";
import { Label } from "@root/shared/components/Label";
import { GuidanceBox } from "@root/shared/components/GuidanceBox";
import * as styles from "./styles.css";

type Option = {
  optionCode: string;
  label: string;
  requiresFollowUpText: boolean;
};

type FormField = {
  name: string;
  followUpTextName: string;
};

type Props = {
  question: ReactNode;
  // 子設問として親の選択肢の下にネスト表示するときのスタイル。
  // 見出しをトップレベルの Heading（medium）ではなく 14px 太字の段落にし、
  // column 配置の選択肢ラベルも 14px に揃える。
  isChildStyle?: boolean;
  // 見出しと選択肢の間に出す補足文言（例: 「※選択済みの休止期間：...」）。
  // 呼び出し元で「出す設問かどうか」を判定して渡す前提。
  note?: ReactNode;
  options: Option[];
  // true で選択肢を 4 列グリッドで並べる。未指定/false で縦積み。
  isGridLayout?: boolean;
  // react-hook-form のフィールド名（チェックボックス群・自由記入欄）
  formField: FormField;
  // 選択肢ごとに、その選択肢を選んだ時にその直下に差し込みたい要素。
  // 表示条件（選択されているかどうか）は呼び出し元で判定して渡す前提で、
  // ここでは受け取ったものをそのまま該当選択肢の下に描画する。
  childrenByOptionCode?: Record<string, ReactNode>;
};

const PLACEHOLDER = "「その他」の場合は、具体的な内容を必ずご入力ください。";

export const MultipleChoiceQuestion: FC<Props> = ({
  question,
  isChildStyle = false,
  note,
  options,
  isGridLayout = false,
  formField,
  childrenByOptionCode,
}) => {
  const {
    register,
    watch,
    formState: { errors },
  } = useFormContext();
  const checkboxError = errors[formField.name];
  const followUpTextError = errors[formField.followUpTextName];

  // 選択中の選択肢に requiresFollowUpText を持つものがあれば自由記入欄を表示する。
  // チェックボックス群が未選択のとき watch は false 等を返し得るため、配列であることを保証する。
  const watchedValue = watch(formField.name);
  const selectedOptionCodes: string[] = Array.isArray(watchedValue) ? watchedValue : [];
  const requiresFollowUpText = options.some(
    (option) => option.requiresFollowUpText && selectedOptionCodes.includes(option.optionCode),
  );

  // 見出しの中身（設問文 + バッジ）は共通で、外側の器（Heading か 14px 太字の段落か）だけ切り替える。
  const headingContent = (
    <>
      {question}
      <Label theme="lightGray" className={styles.headingLabel}>
        複数回答可
      </Label>
      <Label theme="red" className={styles.headingLabel}>
        必須
      </Label>
    </>
  );

  return (
    <>
      {isChildStyle ? (
        <p className={styles.childHeading}>{headingContent}</p>
      ) : (
        <Heading tag="p" size="medium" bold className={styles.heading}>
          {headingContent}
        </Heading>
      )}
      {note && <GuidanceBox className={styles.note}>{note}</GuidanceBox>}
      {checkboxError && (
        <div className={styles.errorText}>
          <InformationText text={checkboxError.message} isAlert />
        </div>
      )}
      <ul className={styles.list({ layout: isGridLayout ? "grid" : "column" })}>
        {options.map((option) => {
          const id = `checkbox-${formField.name}-${option.optionCode}`;
          const childContent = childrenByOptionCode?.[option.optionCode];
          const isFullRow = isGridLayout && option.requiresFollowUpText;
          const columnLabelClass = isChildStyle ? styles.checkboxChildLabel : styles.checkboxLabel;
          const labelClassName = isGridLayout ? styles.checkboxNowrap : columnLabelClass;
          return (
            <li key={option.optionCode} className={styles.listItem({ fullRow: isFullRow })}>
              <Checkbox
                id={id}
                value={option.optionCode}
                label={option.label}
                {...register(formField.name)}
                hasError={!!checkboxError}
                className={styles.checkbox}
                labelClassName={labelClassName}
              />
              {childContent && <div className={styles.childContent}>{childContent}</div>}
            </li>
          );
        })}
      </ul>
      {requiresFollowUpText && (
        <div className={styles.otherTextArea}>
          {followUpTextError && (
            <div className={styles.errorText}>
              <InformationText text={followUpTextError.message} isAlert />
            </div>
          )}
          <TextArea
            {...register(formField.followUpTextName)}
            placeholder={PLACEHOLDER}
            hasError={!!followUpTextError}
          />
        </div>
      )}
    </>
  );
};
